import { useSelector } from 'react-redux';
import {
    useGetAccountQuery,
    useGetPaymentIntegrationQuery,
    useGetTenantAccountQuery,
} from '../api/account/accountApiSlice';
import {
    AccountTenant,
    AccountUser,
    TenantPaymentIntegration,
} from '../api/account/accountApiTypes';
import { useIsMaster } from '../common/useIsMaster';
import { tenantSelector } from '../selector/authenticationSelector';
import { isSelfServiceTenant } from '../common/tenantStore';

export interface ViewedTenantAccount {
    /** True for an operational (self-service) tenant. Legacy tenants have no
     *  `app_user` row, so none of these endpoints resolve for them. */
    isSelfService: boolean;
    isMaster: boolean;
    /** The caller OWNS the tenant currently on screen. */
    ownsViewedTenant: boolean;
    /** The tenant IN VIEW — for a master on another tenant the by-slug tenant,
     *  never the caller's own. */
    accountTenant: AccountTenant | undefined;
    /** The caller's own member record, absent on a pure master view. */
    accountUser: AccountUser | undefined;
    /** Editing is scoped to the owner of the tenant on screen. */
    readOnly: boolean;
    /** Derived Stripe status of the tenant in view. Never chosen, never stored. */
    paymentIntegration: TenantPaymentIntegration | undefined;
    /** Identity of the tenant in view — the key a form seeds itself from, so a
     *  tenant switch re-seeds while an unrelated cache invalidation does not. */
    tenantKey: string | undefined;
    /**
     * The read that decides `accountTenant` is in flight and has not answered.
     *
     * A gate must be able to tell "not yet known" from "known to be absent":
     * answering a pending read as `false` is how a redirect strands a user on a
     * page the later answer would have taken them off (ADR-0001 Amendment 4).
     * `false` for a legacy tenant, whose reads are skipped entirely.
     */
    isResolving: boolean;
    /**
     * That read ran and did not answer. Separate from `isResolving`, because a
     * caller that waits must be able to stop waiting — and because a failure
     * must never be rendered as an absence.
     */
    hasFailed: boolean;
}

/**
 * Which tenant am I looking at, may I edit it, and what does it have deposited.
 *
 * ONE definition, because getting it wrong is invisible: a screen that answers
 * it with a bare `useGetAccountQuery()` always resolves to the CALLER's own
 * tenant, so a master viewing another tenant sees their own configuration under
 * that tenant's name (#1334, #2059). Both the Account page and the Services
 * area need the same resolution, so neither re-derives it.
 *
 * The three cases it collapses:
 *   - a normal member  → their own account, editable only as `owner`
 *   - a master on their OWN tenant → the own (writable) account: the account
 *     PATCH endpoints resolve their target by the caller's auth0_sub, so a
 *     by-slug read here would show one tenant and write another
 *   - a master on ANOTHER tenant → the by-slug read, read-only
 */
export interface ViewedTenantAccountOptions {
    /**
     * Whether to resolve the Stripe connection status too. Defaults to `true`,
     * which is what the configuration screens need.
     *
     * Pass `false` from a screen that only wants to know WHICH tenant it is
     * looking at: `payment-integration` reads live from Stripe, so asking for it
     * on a screen that never shows it buys a round-trip per visit and nothing
     * else. `paymentIntegration` is then `undefined` — the same value a legacy
     * tenant always gets — so a caller must not read it after opting out.
     */
    withPaymentIntegration?: boolean;
}

/**
 * The settled-ness of one RTK Query read, in the two terms a gate needs: still
 * coming, or came back empty-handed. Extracted so the hook below picks a branch
 * once instead of repeating the same expression per field (and so the rule is
 * stated in one place, as `useEntitlement` and `useCanAccessAccount` state it).
 *
 * A skipped query reports neither loading nor error, so it settles to
 * `{ false, false }` — which is what a legacy tenant, whose reads never run,
 * must resolve to.
 */
interface ReadSettlement {
    isResolving: boolean;
    hasFailed: boolean;
}

const readSettlement = (
    data: unknown,
    isLoading: boolean,
    isFetching: boolean,
    isError: boolean,
): ReadSettlement => ({
    isResolving: !data && (isLoading || isFetching),
    hasFailed: isError && !data,
});

export const useViewedTenantAccount = (
    { withPaymentIntegration = true }: ViewedTenantAccountOptions = {},
): ViewedTenantAccount => {
    const isMaster = useIsMaster();
    const selectedTenant = useSelector(tenantSelector);
    const isSelfService = isSelfServiceTenant(selectedTenant);

    // Legacy tenants have no operational account record, so none of these
    // endpoints resolve for them — skip rather than fire a failing request on
    // every visit (the Services page, unlike Account, is reachable for legacy
    // SUP tenants, whose deposit section is all it holds).
    const {
        data: ownAccount,
        isLoading: ownLoading,
        isFetching: ownFetching,
        isError: ownError,
    } = useGetAccountQuery(undefined, { skip: !isSelfService });
    const {
        data: masterAccount,
        isLoading: masterLoading,
        isFetching: masterFetching,
        isError: masterError,
    } = useGetTenantAccountQuery(selectedTenant as string, {
        skip: !isMaster || !isSelfService || !selectedTenant,
    });

    // A pure internal master with no `app_user` row resolves to
    // `onboardingPending` (no `user` / `tenant`), which narrows away here.
    const ownAccountData = ownAccount && 'user' in ownAccount ? ownAccount : undefined;

    const ownsViewedTenant =
        ownAccountData?.user.role === 'owner' &&
        !!ownAccountData.tenant.slug &&
        ownAccountData.tenant.slug === selectedTenant;

    const account = isMaster ? (ownsViewedTenant ? ownAccountData : masterAccount) : ownAccount;

    const { data: paymentIntegration } = useGetPaymentIntegrationQuery(
        isMaster && !ownsViewedTenant ? (selectedTenant as string) : undefined,
        { skip: !isSelfService || !withPaymentIntegration },
    );

    // The union member without `user` narrows to never, so read it through an
    // optional-shaped view.
    const accountUser = (account as { user?: AccountUser } | undefined)?.user;
    const accountTenant: AccountTenant | undefined =
        account && !('onboardingPending' in account) ? account.tenant : undefined;

    const readOnly = isMaster ? !ownsViewedTenant : !(accountUser?.role === 'owner');

    // Which of the two reads actually decides what is returned — the same
    // branch `account` takes above, so what a caller waits for and what it
    // reads cannot be different queries.
    const usesMasterRead = isMaster && !ownsViewedTenant;
    const ownSettlement = readSettlement(ownAccount, ownLoading, ownFetching, ownError);
    const branchSettlement = usesMasterRead
        ? readSettlement(masterAccount, masterLoading, masterFetching, masterError)
        : ownSettlement;
    // For a master, WHICH read applies is itself decided by the own read
    // (`ownsViewedTenant`). While it is in flight the answer is not known yet:
    // a master read that lands first would report a master on their OWN tenant
    // as read-only until the own read arrives.
    const isResolving = branchSettlement.isResolving || (isMaster && ownSettlement.isResolving);
    const { hasFailed } = branchSettlement;

    return {
        isSelfService,
        isMaster,
        ownsViewedTenant,
        accountTenant,
        accountUser,
        readOnly,
        paymentIntegration,
        tenantKey: accountTenant?.slug ?? undefined,
        isResolving,
        hasFailed,
    };
};
